import React, { useRef, useState, useCallback } from "react";
import { Button, Box, Typography } from "@mui/material";
import SignatureCanvas from "react-signature-canvas";
import PropTypes from "prop-types";
import StandardModal from "../Modals/StandardModal";

/**
 * A React component for capturing and managing user signatures via a modal.
 * Utilizes the StandardModal component for consistent styling.
 *
 * @param {Object} props - The component props.
 * @param {string} props.name - The name of the signature field (used for synthetic events).
 * @param {Function} props.onChange - Callback function to handle changes in signature data.
 * Receives a synthetic event with `name` and `value` (JSON paths).
 *
 * @returns {JSX.Element} The rendered SignatureField component.
 */
function SignatureField({ name, onChange }) {
    const [open, setOpen] = useState(false);
    const [signatureData, setSignatureData] = useState(null);
    const [signatureJSON, setSignatureJSON] = useState("");
    const [error, setError] = useState("");
    const sigCanvasRef = useRef(null);

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        if (error) {
            setError("");
            handleClear();
        }

        setOpen(false);
    }

    const handleClear = useCallback(() => {
        if (sigCanvasRef.current) {
            sigCanvasRef.current.clear();
        }
        setSignatureJSON("");
        setSignatureData(null);

        onChange({
            target: {
                name,
                value: "",
            },
        });
    }, [sigCanvasRef, name, onChange]);

    const handleConfirm = useCallback(() => {
        if (sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) {
            const json = JSON.stringify(sigCanvasRef.current.toData());
            const base64 = sigCanvasRef.current.getTrimmedCanvas().toDataURL("image/png");

            setSignatureData(base64);
            setSignatureJSON(json);

            if (json.length > 40000) {
                setError("Your signature is too large or complex, please clear it and try again.");
                return;
            }

            onChange({
                target: {
                    name,
                    value: json,
                },
            });


            handleClose();
        }
    }, [sigCanvasRef, name, onChange]);

    return (
        <div>
            <button
                type="button"
                className="button primary expanded"
                onClick={handleOpen}
            >
                Click here to sign
            </button>

            <div className="margin-top-1">
                <SignaturePreview signatureData={signatureData} />
            </div>

            {signatureData && (
                <button
                    type="button"
                    className="button alert margin-top-1"
                    onClick={handleClear}
                >
                    Clear Signature
                </button>
            )}

            <input type="hidden" name={name} value={signatureJSON} />

            <StandardModal
                open={open}
                onClose={handleClose}
                title="Sign Here"
                buttons={[
                    <button
                        key="save"
                        type="button"
                        className="button primary"
                        onClick={handleConfirm}
                    >
                        Save Signature
                    </button>,
                    <button
                        key="cancel"
                        className="button secondary"
                        onClick={handleClose}
                    >
                        Cancel
                    </button>,
                ]}
                secondaryButtons={[
                    <button
                        key="clear"
                        type="button"
                        className="button alert"
                        onClick={handleClear}
                    >
                        Clear signature
                    </button>,
                ]}
                isScrollable
            >
                <SignatureCanvas
                    ref={sigCanvasRef}
                    penColor="black"
                    canvasProps={{
                        style: {
                            border: "1px solid #ccc",
                            borderRadius: "8px",
                            width: "100%",
                            height: "300px",
                        },
                    }}
                />
                {error && <ErrorMessage message={error} />}
            </StandardModal>
        </div>
    );
}

SignatureField.propTypes = {
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
};

/**
 * Simple component to display a signature image preview.
 *
 * @param {Object} props
 * @param {string} props.signatureData
 * @returns {JSX.Element}
 */
const SignaturePreview = ({ signatureData }) => {
    if (typeof signatureData === "string" && signatureData.length > 0) {
        return (
            <img
                src={signatureData}
                alt="Signature"
                style={{
                    border: "1px solid #ccc",
                    borderRadius: "8px",
                    width: "100%",
                    height: "150px",
                    objectFit: "contain",
                }}
            />
        );
    }
    return (
        <Typography variant="body2" color="textSecondary">
            No signature yet
        </Typography>
    );
};

SignaturePreview.propTypes = {
    signatureData: PropTypes.string,
};

/**
 * Error message component
 *
 * @param {string} message
 * @returns {JSX.Element}
 */
const ErrorMessage = ({ message }) => (
    <div>
        <small className="error">{message}</small>
    </div>
);

ErrorMessage.propTypes = {
    message: PropTypes.string.isRequired,
};

export default SignatureField;
